import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 監聽 Supabase Auth 狀態
  useEffect(() => {
    const loadUserInfo = async (userId: string) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('name, role, teacher_status')
        .eq('id', userId)
        .maybeSingle();
      setUserName(profile?.name || '');
      setIsAdmin(profile?.role === 'admin');
      setIsTeacher(profile?.role === 'teacher' && profile?.teacher_status === 'approved');
    };

    // 初始化時取得目前 session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setIsLoggedIn(true);
        loadUserInfo(session.user.id);
      } else {
        setIsLoggedIn(false);
        setUserName('');
        setIsAdmin(false);
        setIsTeacher(false);
      }
    });

    // 監聽登入/登出事件
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setIsLoggedIn(true);
        loadUserInfo(session.user.id);
      } else {
        setIsLoggedIn(false);
        setUserName('');
        setIsAdmin(false);
        setIsTeacher(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // 購物車數量
  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const total = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
      setCartCount(total);
    };
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, [location]);

  // 點擊外部關閉下拉選單（修正：加入 touchstart 支援行動裝置）
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // 換頁時自動關閉所有選單
  useEffect(() => {
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setUserName('');
    setIsAdmin(false);
    setIsTeacher(false);
    setShowUserMenu(false);
    navigate('/login');
  };

  const navLinks = [
    { name: '首頁', path: '/' },
    { name: '香氛實驗室', path: '/workshop' },
    { name: '線上商城', path: '/shop' },
    { name: '部落格', path: '/blog' },
    { name: '關於我們', path: '/about' }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/30 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20 gap-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0 min-w-0">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/c0ff92e5a82252bc47aa78b3b10aa452.png"
                alt="MOFU Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-lg lg:text-2xl font-bold text-gray-800 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="hidden sm:inline">MOFU 淨寵居</span>
              <span className="sm:hidden">MOFU</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8 flex-1 justify-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-coral-500 cursor-pointer whitespace-nowrap ${
                  location.pathname === link.path ? 'text-coral-500' : 'text-gray-700'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 lg:gap-4 flex-shrink-0">
            <Link to="/cart" className="relative cursor-pointer w-9 h-9 flex items-center justify-center">
              <i className="ri-shopping-cart-line text-2xl text-gray-700 hover:text-coral-500 transition-colors"></i>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-coral-500 text-white text-xs rounded-full flex items-center justify-center whitespace-nowrap">
                  {cartCount}
                </span>
              )}
            </Link>

            {isLoggedIn ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-1 lg:gap-2 px-2 lg:px-3 py-2 text-gray-700 hover:text-coral-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-coral-100 rounded-full flex-shrink-0">
                    <i className="ri-user-line text-coral-500 text-base"></i>
                  </div>
                  <span className="hidden md:inline text-sm font-medium max-w-[80px] truncate">
                    {userName || '會員'}
                  </span>
                  <i className="ri-arrow-down-s-line text-lg hidden md:inline"></i>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl py-2 border border-gray-100 z-50">
                    <div className="px-4 py-2 border-b border-gray-100 mb-1">
                      <p className="text-xs text-gray-400">已登入</p>
                      <p className="text-sm font-semibold text-gray-800 truncate">{userName || '會員'}</p>
                    </div>

                    <Link
                      to="/member"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-coral-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-user-settings-line text-base"></i>
                      我的會員中心
                    </Link>
                    <Link
                      to="/cart"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-coral-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-shopping-cart-line text-base"></i>
                      我的購物車
                    </Link>

                    {/* 老師後台入口 */}
                    {isTeacher && (
                      <>
                        <div className="border-t border-gray-100 my-1"></div>
                        <Link
                          to="/teacher"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-2 px-4 py-3 text-sm text-purple-600 hover:bg-purple-50 transition-colors cursor-pointer whitespace-nowrap font-medium"
                        >
                          <i className="ri-book-open-line text-base"></i>
                          老師後台
                        </Link>
                      </>
                    )}

                    {/* 只有 admin 才顯示後台管理 */}
                    {isAdmin && (
                      <>
                        <div className="border-t border-gray-100 my-1"></div>
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-2 px-4 py-3 text-sm text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap font-medium"
                        >
                          <i className="ri-shield-user-line text-base"></i>
                          後台管理
                        </Link>
                      </>
                    )}

                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-500 hover:bg-gray-50 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-logout-box-line text-base"></i>
                      登出
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1 lg:gap-2 px-3 lg:px-4 py-2 bg-coral-500 text-white rounded-full text-sm font-medium hover:bg-coral-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-line text-base"></i>
                <span className="hidden sm:inline">登入</span>
                <span className="sm:hidden">登入</span>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center text-gray-700 hover:text-coral-500 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-3 text-sm font-medium transition-colors hover:text-coral-500 cursor-pointer whitespace-nowrap ${
                  location.pathname === link.path ? 'text-coral-500' : 'text-gray-700'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {isTeacher && (
              <Link
                to="/teacher"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 text-sm font-medium text-purple-600 hover:text-purple-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line mr-2"></i>
                老師後台
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-3 text-sm font-medium text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shield-user-line mr-2"></i>
                後台管理
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
